import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  importProvidersFrom,
  inject,
  Injectable,
  LOCALE_ID,
  OnDestroy,
  provideAppInitializer,
  provideZoneChangeDetection,
} from "@angular/core";
import { provideRouter, Router, withComponentInputBinding, withRouterConfig } from "@angular/router";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { routes } from "./app.routes";
import { providePrimeNG } from "primeng/config";
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from "@angular/common/http";
import { environment } from "@environments/environment";
import { initializeTranslation, provideTranslation } from "./kernel/translations/translation.config";
import { TranslationService } from "./kernel/translations/translation.service";
import { ELocalizationLang } from "./shared-core/enums/localization-lang.enum";
import { LocalStorageService } from "./kernel/tools/services/local-storage.service";
import { registerLocaleData } from "@angular/common";
import { provideMessage } from "./kernel/tools/message/message.config";
import { provideSideNav } from "./shared/general/sidenav/providers/sidenav";
import { provideGraphQL } from "./kernel/graphql/graphql.config";
import { provideConfirmationDialog } from "./kernel/popups/popup/modules/confirmation-dialog/confirmation-dialog.config";
import { JwtModule } from "@auth0/angular-jwt";
import { provideNotifications } from "./kernel/notifications/notification.config";
import { provideFirebase } from "./kernel/notifications/filrebase.config";
import { NotificationApiService } from "./shared/general/notifications/services/notification-api.service";
import { errorInterceptor } from "./shared/general/interceptors/error.interceptor";
import { AuthService } from "./shared/auth/services/auth.service";
import { AuthService as BaseAuthService } from "./kernel/tools/services/auth.service";
import { pendingRequestsInterceptor$ } from "ng-http-loader";
import { jwtInterceptor } from "./shared-core/interceptors/jwt.interceptor";
import { TopNavService } from "./shared/general/top-nav/services/top-nav.service";
import { TopNavAbstraction } from "./shared-core/abstractions/top-nav.abstraction";
import { ActiveDirectoryAbstraction } from "./shared-core/abstractions/active-directory.abstraction";
import { ActiveDirectoryService } from "./shared/company-hierarchy/directories/services/active-directory.service";
import { laraTheme } from "@shared-core/config/primeng";
import { MaintenanceBreakService } from "./shared/settings/system-settings/services/maintenance-break.service";
import { authInitializer } from "./shared/auth/initializers/auth.initializer";
import { SOCKET_ERROR_INTERCEPTOR } from "./shared/general/socket-io/tokens/socket-error-interceptor.token";
import { WsMaintenanceBreakErrorInterceptor } from "./shared/settings/system-settings/ws-interceptors/ws-maintenance-break-error.interceptor";
import { newsInterceptor } from "./shared/general/interceptors/news.interceptor";
import { eventManagersProviders } from "./kernel/plguins";
import { provideDynamicTabs } from "./shared/general/dynamic-tabs/dynamic-tabs.providers";
import * as Sentry from "@sentry/angular";
import { sentryUserContextInitializer } from "./shared-core/Initializers/sentry-user-context.Initializer";
import { sentryFeedbackInitializer } from "./shared-core/Initializers/sentry-feedback.Initializer";
import { sentryReportDialogInitializer } from "./shared-core/Initializers/sentry-report-dialog.Initializer";
import { provideEnabledApplications } from "./configs/applications/applications.config";

const locales = {
  en: () => import("@angular/common/locales/en"),
  it: () => import("@angular/common/locales/it"),
};

export const loadLocale = () => {
  const localStorage = inject(LocalStorageService);
  const lang = localStorage.get<ELocalizationLang>("language") || ELocalizationLang.EN;
  locales[lang]().then(d => registerLocaleData(d.default));
  return lang;
};

@Injectable()
class SentryErrorHandler implements ErrorHandler, OnDestroy {
  private sentryErrorHandler = Sentry.createErrorHandler();
  handleError(error: unknown): void {
    if (error === "Timeout") return;
    this.sentryErrorHandler.handleError(error);
  }
  ngOnDestroy(): void {
    this.sentryErrorHandler.ngOnDestroy();
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    ...eventManagersProviders,
    provideEnabledApplications(),
    provideAppInitializer(() => authInitializer(inject(AuthService), inject(MaintenanceBreakService))),
    provideAppInitializer(() => {
      provideSideNav();
      provideDynamicTabs();
      return initializeTranslation(inject(TranslationService));
    }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({
        onSameUrlNavigation: "reload",
        paramsInheritanceStrategy: "always",
      })
    ),
    provideMessage(),
    providePrimeNG({
      theme: {
        preset: laraTheme,
        options: {
          darkModeSelector: ".my-app-dark",
        },
      },
    }),
    {
      provide: LOCALE_ID,
      useFactory: loadLocale,
      deps: [LocalStorageService],
    },
    provideGraphQL(environment.graphql_endpoint),
    provideTranslation(),
    // Firebase setup
    ...provideFirebase(),
    provideConfirmationDialog(),
    importProvidersFrom(
      JwtModule.forRoot({
        config: { tokenGetter: _ => null },
      })
    ),
    {
      provide: SOCKET_ERROR_INTERCEPTOR,
      useClass: WsMaintenanceBreakErrorInterceptor,
      multi: true,
    },
    {
      provide: BaseAuthService,
      useExisting: AuthService,
    },
    {
      provide: TopNavAbstraction,
      useExisting: TopNavService,
    },
    {
      provide: ActiveDirectoryAbstraction,
      useExisting: ActiveDirectoryService,
    },
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([pendingRequestsInterceptor$, jwtInterceptor, errorInterceptor, newsInterceptor])),
    provideNotifications({ notificationApiImpl: NotificationApiService }),
    {
      provide: LOCALE_ID,
      useFactory: loadLocale,
      deps: [LocalStorageService],
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: "EUR",
    },
    provideZoneChangeDetection({ eventCoalescing: true }),
    { provide: ErrorHandler, useValue: new SentryErrorHandler() },
    { provide: Sentry.TraceService, deps: [Router] },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideAppInitializer(() => sentryUserContextInitializer()),
    provideAppInitializer(() => sentryFeedbackInitializer()),
    provideAppInitializer(() => sentryReportDialogInitializer()),
  ],
};
